import type { Filter, TeamOut } from '@repo/api-codegen';
import { fetchApiListPolicies } from '@repo/api-codegen';
import {
	apiListTeamsV2QueryKey,
	fetchApiListTeamsV2,
} from '@repo/api-codegen/v2';

import type {
	FilterItem,
	FilterOption,
	FilterValue,
	FilterValueType,
} from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
	FilterOptionType,
	FilterType,
} from '@repo/common/components/Filter/types';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import ItemIcon from '@repo/common/components/ItemIcon';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import TagIcon from '@repo/common/components/TagIcon/TagIcon';
import {
	METRIC_TYPE_INFORMATION,
	MONITOR_ICON_MAPPING,
} from '@repo/common/constants';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { DataQualityClassification } from '@repo/common/enums/dataQualityClassification';
import { EntityType } from '@repo/common/enums/entityType';
import { getScoreClassificationFromFilter } from '@repo/common/utils/dataQuality';
import { getDataQualityClassificationCatalogFilter } from '@repo/common/utils/getDataQualityClassificationCatalogFilter';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';
import {
	includes,
	lowerCase,
	map,
	sortBy,
	startCase,
	uniqBy,
	uniqWith,
} from 'lodash-es';
import type { ICollection, IIntegration, ISlackChannel, ITag } from '../../api';
import {
	CATALOG_ENTITY_TYPES,
	COLLECTIONS_NAMESPACE,
	collectionsQueryKeyFactory,
	fetchCollectionList,
	fetchIntegrationList,
	fetchTagList,
	INTEGRATIONS_NAMESPACE,
	integrationsQueryKeyFactory,
	queryClient,
	TAGS_NAMESPACE,
	tagsQueryKeyFactory,
} from '../../api';
import { POLICIES_NAMESPACE } from '../../api/hooks/policy';
import { fetchSecodaEntity } from '../../api/hooks/secodaEntity/fetchSecodaEntities';
import { slackChannelsQuery } from '../../api/hooks/workspace/useSlackChannels';
import { type SecodaEntity } from '../../lib/models';
import { getEntityDisplayTitle } from '../../lib/utils/entity';
import {
	getEntityFilters,
	getSearchResults,
} from '../../pages/SearchPage/utils';
import { captureError } from '../../web-tracing';
import { MONITOR_SPECS } from '../AddMonitorV2/monitors';
import { isAnalyticsMonitor } from '../AddMonitorV2/types';
import { StatusColorMap } from '../DataAccessRequest/DataAccessRequestStatusBadge/statusColorMap';
import IntegrationLogo from '../IntegrationLogo';
import { SecodaEntityIcon } from '../SecodaEntity';
import {
	aiPersonasGetItemsById,
	aiPersonasGetItemsFn,
	allUsersAndGroupsGetItemsFn,
	buildEntityGetItemsFn,
	buildGetItemsById,
	buildGetItemsByIdFromSearch,
	convertBooleanFilterToIsSetFilter,
	convertLineageFilterToCatalogFilter,
	convertMonitorFilterToCatalogFilter,
	convertOwnerFilterFromCatalogFilter,
	convertOwnerFilterToCatalogFilter,
	createdByGetItemsFn,
	fetchQuery,
	renderFilterItemWithBreadcrumbs,
	usersGetItemsByIdFn,
} from './filterConfigUtils';

// Helper function to generate depth items dynamically based on MAX_LINEAGE_DEPTH
export function generateDepthItems(maxDepth: number): FilterItem[] {
	return Array.from({ length: maxDepth }, (_, i) => ({
		label: `${i + 1} level${i > 0 ? 's' : ''}`,
		value: i + 1,
	}));
}

// Default depth options (fallback if memberEnv is not available)
const DEFAULT_MAX_DEPTH = 10;
export const DEFAULT_DEPTH_ITEMS = generateDepthItems(DEFAULT_MAX_DEPTH);

/**
 * Get depth items for lineage filters based on memberEnv.
 * If workspace lineage is not available, limits to depth 1 only.
 *
 * @param maxDepth - Maximum depth from memberEnv (1 if lineage not generated, 10 if available)
 * @returns Array of filter items for depth selection
 */
export function getLineageDepthItems(maxDepth?: number): FilterItem[] {
	if (maxDepth === undefined) {
		return DEFAULT_DEPTH_ITEMS;
	}
	return generateDepthItems(maxDepth);
}

// As part of ENG-18084, these values are displayed as
// Today
// Last 7 days
// Last 30 days
// Last 60 days
// Last 90 days
// We use the "Is" operator as opposed to "Is on or after"
// and the "Is not" operator as opposed to "Is on or before"
// To make this work as expected, the filter operator in the backend is always "gt" (the "lt" case is handled by a "not" operator)
export const DATE_LIST_OPTIONS = [
	{
		label: 'Today',
		value: 'ONE_DAY_AGO',
	},
	{
		label: 'Last 7 days',
		value: 'SEVEN_DAYS_AGO',
	},
	{
		label: 'Last 30 days',
		value: 'THIRTY_DAYS_AGO',
	},
	{
		label: 'Last 60 days',
		value: 'SIXTY_DAYS_AGO',
	},
	{
		label: 'Last 90 days',
		value: 'NINETY_DAYS_AGO',
	},
	{
		label: 'Custom',
		value: 'CUSTOM',
	},
];

export const CHILD_FILTER_OPTIONS = [
	{
		label: 'Verified',
		value: 'verified',
	},
	{
		label: 'Marked as PII',
		value: 'pii',
	},
	{
		label: 'Published',
		value: 'published',
	},
	{
		label: 'Tags',
		value: 'tags',
	},
	{
		label: 'Collections',
		value: 'collections',
	},
	{
		label: 'Owners',
		value: 'owners',
	},
	{
		label: 'Teams',
		value: 'teams',
	},
];

export const SEMANTIC_VIEW_TYPES_COLUMN_TYPES = [
	// Snowflake semantic views
	{ label: 'Metric', value: 'snowflake_semantic_metric' },
	{ label: 'Dimension', value: 'snowflake_semantic_dimension' },
	{ label: 'Fact', value: 'snowflake_semantic_fact' },
	{ label: 'Time dimension', value: 'snowflake_semantic_time_dimension' },
	// dbt semantic models
	{ label: 'Measure', value: 'dbt_semantic_measure' },
	{ label: 'Dimension', value: 'dbt_semantic_dimension' },
	{ label: 'Entity', value: 'dbt_semantic_entity' },
];

export const SEMANTIC_VIEW_TYPES_GENERIC_TYPES = [
	{ label: 'Filter', value: 'snowflake_semantic_filter' },
	{ label: 'Relationship', value: 'snowflake_semantic_relationship' },
];

export const FILTER_OPTIONS_CONFIG: Record<string, FilterOption> = {
	[FilterOptionType.NATIVE_TYPE]: {
		label: 'Type',
		type: FilterOptionType.NATIVE_TYPE,
		field: 'native_type',
		filterDropdownConfig: {
			searchPlaceholder: 'Search types',
			multipleCountsSuffix: 'types',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => {
				const filters = await getEntityFilters();
				const nativeTypes = sortBy(uniqBy(filters.native_types, 'label'), [
					(nativeType) => lowerCase(nativeType.value as string),
				]);
				const nativeTypeFilters = nativeTypes?.map((n) => ({
					label: n.label,
					value: n.value,
					entityType: n.entityType,
				}));
				return uniqWith(
					nativeTypeFilters,
					(a, b) => a.value === b.value && a.entityType === b.entityType
				).filter(
					(item) =>
						!searchTerm ||
						item.label.toLowerCase().includes(searchTerm.toLowerCase())
				);
			},
		},
	},
	[FilterOptionType.SEMANTIC_VIEW_COLUMN_TYPE]: {
		label: 'Semantic type',
		type: FilterOptionType.SEMANTIC_VIEW_COLUMN_TYPE,
		field: 'native_type',
		filterDropdownConfig: {
			searchPlaceholder: 'Search semantic types',
			multipleCountsSuffix: 'semantic view types',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => [
				...SEMANTIC_VIEW_TYPES_COLUMN_TYPES.filter(
					(item) =>
						!searchTerm ||
						item.label.toLowerCase().includes(searchTerm.toLowerCase())
				),
			],
		},
	},
	[FilterOptionType.SEMANTIC_VIEW_GENERIC_TYPE]: {
		label: 'Semantic type',
		type: FilterOptionType.SEMANTIC_VIEW_GENERIC_TYPE,
		field: 'native_type',
		filterDropdownConfig: {
			searchPlaceholder: 'Search semantic types',
			multipleCountsSuffix: 'semantic view types',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => [
				...SEMANTIC_VIEW_TYPES_GENERIC_TYPES.filter(
					(item) =>
						!searchTerm ||
						item.label.toLowerCase().includes(searchTerm.toLowerCase())
				),
			],
		},
	},
	[FilterOptionType.INTEGRATION]: {
		label: 'Integration',
		type: FilterOptionType.INTEGRATION,
		field: 'integration_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search integrations',
			multipleCountsSuffix: 'integrations',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
					integrationsQueryKeyFactory.list(filterPage, {
						search_term: filterSearchTerm,
					});
				const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
					fetchIntegrationList({
						page: filterPage,
						filters: { search_term: filterSearchTerm },
					});

				const filterFn = (filterSearchTerm: string, result: IIntegration[]) =>
					result.filter((integration) => {
						const spec = integrationList.find(
							(i) => i.type === integration.type
						);
						return (
							!spec?.isExtension &&
							(!searchTerm ||
								integration.name
									.toLowerCase()
									.includes(filterSearchTerm.toLowerCase()))
						);
					});

				// Fetch results without pre-filtering. Passing a no-op filter avoids
				// double-filtering in cached vs fresh paths; we apply the real filter below.
				const results = await fetchQuery<IIntegration>(
					getQueryKey,
					getQueryFn,
					page,
					searchTerm,
					(_t, r) => r
				);

				// Apply the actual filter locally so extensions are hidden consistently
				// on first load and when data is cached.
				const filteredResults = filterFn(searchTerm, results ?? []);

				return (
					filteredResults?.map((integration) => ({
						label: integration.name,
						value: integration.id,
						icon: (
							<IntegrationLogo
								entityType={EntityType.integration}
								integrationId={integration.id}
								integrationType={integration.type}
								size={16}
							/>
						),
					})) ?? []
				);
			},
			getItemsById: buildGetItemsById<IIntegration>(
				INTEGRATIONS_NAMESPACE,
				(integration) => (
					<IntegrationLogo
						entityType={EntityType.integration}
						integrationId={integration.id}
						integrationType={integration.type}
						size={16}
					/>
				)
			),
		},
	},
	[FilterOptionType.INTEGRATION_TYPE]: {
		label: 'Integration type',
		type: FilterOptionType.INTEGRATION_TYPE,
		field: 'integration_type',
		filterDropdownConfig: {
			searchPlaceholder: 'Search integrations',
			multipleCountsSuffix: 'integrations',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				// Use the standard paginated fetch helper (same pattern as other filters)
				const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
					integrationsQueryKeyFactory.list(filterPage, {
						search_term: filterSearchTerm,
					});
				const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
					fetchIntegrationList({
						page: filterPage,
						filters: { search_term: filterSearchTerm },
					});

				const filterFn = (filterSearchTerm: string, result: IIntegration[]) =>
					result.filter((integration) => {
						const spec = integrationList.find(
							(i) => i.type === integration.type
						);
						return (
							!spec?.isExtension &&
							integration.type
								.toLowerCase()
								.includes(filterSearchTerm.toLowerCase())
						);
					});

				const results = await fetchQuery<IIntegration>(
					getQueryKey,
					getQueryFn,
					page,
					searchTerm,
					filterFn
				);

				return (
					uniqBy(results, 'type')?.map((integration) => ({
						label: integration.type,
						value: integration.type,
						icon: (
							<IntegrationLogo
								entityType={EntityType.integration}
								integrationId={integration.id}
								integrationType={integration.type}
								size={16}
							/>
						),
					})) ?? []
				);
			},
			getItemsById: async (types: string[]): Promise<FilterItem[]> => {
				const getQueryKey = (filterPage: number) =>
					integrationsQueryKeyFactory.list(filterPage);
				const getQueryFn = (filterPage: number) =>
					fetchIntegrationList({
						page: filterPage,
					});
				const filterFn = (filterSearchTerm: string, result: IIntegration[]) =>
					result.filter((integration) => types.includes(integration.type));

				const results = await fetchQuery<IIntegration>(
					getQueryKey,
					getQueryFn,
					1,
					'',
					filterFn
				);

				return results.map((integration) => ({
					label: integration.type,
					value: integration.type,
					icon: (
						<IntegrationLogo
							entityType={EntityType.integration}
							integrationId={integration.id}
							integrationType={integration.type}
							size={16}
						/>
					),
				}));
			},
		},
	},
	[FilterOptionType.DATABASE]: {
		label: 'Database',
		type: FilterOptionType.DATABASE,
		field: 'database',
		filterDropdownConfig: {
			searchPlaceholder: 'Search databases',
			multipleCountsSuffix: 'databases',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => {
				const filters = await getEntityFilters();
				return uniqBy(filters.databases, 'value')
					.map((item) => ({
						label: item.label,
						value: item.value,
					}))
					.filter(
						(item) =>
							!searchTerm ||
							item.label.toLowerCase().includes(searchTerm.toLowerCase())
					);
			},
		},
	},
	[FilterOptionType.SCHEMA]: {
		label: 'Schema',
		type: FilterOptionType.SCHEMA,
		field: 'parent_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search schemas',
			multipleCountsSuffix: 'schemas',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: buildEntityGetItemsFn(EntityType.schema),
			getItemsById: buildGetItemsByIdFromSearch,
			renderMenuItem: renderFilterItemWithBreadcrumbs(false),
			convertToCatalogFilter: async (value: FilterValueType | null) => {
				if (!value) return null;

				// Support single or multiple values from the dropdown
				const values = Array.isArray(value) ? value : [value];
				const ids = values.filter(Boolean) as string[];

				if (ids.length === 0) return null;

				// Build: OR (id=<id>, parent_id=<id>) ... for each selected id
				const operands = ids.flatMap((v) => [
					{ operands: [], operator: 'exact' as const, field: 'id', value: v },
					{
						operands: [],
						operator: 'exact' as const,
						field: 'parent_id',
						value: v,
					},
				]);

				return { operator: 'or' as const, operands };
			},

			convertFromCatalogFilter: async (filter, isNegate = false) => {
				if (!filter) return null;

				// Recursive reader will pass single 'exact' operands when descending into an OR
				if (
					filter.operator === 'exact' &&
					(filter.field === 'id' || filter.field === 'parent_id') &&
					typeof filter.value === 'string'
				) {
					try {
						const entity = await fetchSecodaEntity(filter.value);
						if (entity?.entity_type === 'schema') {
							return {
								filterType: FilterOptionType.SCHEMA,
								value: entity.id,
								operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
							};
						}
					} catch (e) {
						captureError(e);
					}
					return null;
				}

				const fields = (filter.operands ?? []).map((o) => o.field);
				const isLegacy =
					filter.operator === 'and' &&
					((fields.length === 2 &&
						fields.includes('integration_id') &&
						fields.includes('schema')) ||
						(fields.length === 3 &&
							fields.includes('integration_id') &&
							fields.includes('schema') &&
							fields.includes('database')));

				if (!isLegacy) return null;

				const integrationId = filter.operands.find(
					(o) => o.field === 'integration_id'
				)?.value;
				const schemaTitle = filter.operands.find(
					(o) => o.field === 'schema'
				)?.value;
				const database = filter.operands.find(
					(o) => o.field === 'database'
				)?.value;

				const getItemsFn = buildEntityGetItemsFn(EntityType.schema);
				const items = (await getItemsFn(undefined, String(schemaTitle))) ?? [];
				const found = items.find(
					(item) =>
						item.metadata?.integration === integrationId &&
						item.metadata?.title === schemaTitle &&
						(database
							? item.metadata?.search_metadata?.database === database
							: true)
				);

				if (found) {
					return {
						filterType: FilterOptionType.SCHEMA,
						value: found.value,
						operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
					};
				}
				return null;
			},
		},
	},
	[FilterOptionType.SCHEMA_NAME]: {
		label: 'Schema name (Legacy)',
		type: FilterOptionType.SCHEMA_NAME,
		field: 'schema',
		filterDropdownConfig: {
			multipleCountsSuffix: 'schemas',
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Is,
			inputPlaceholder: 'Enter a schema name',
			hasIsNotSetOption: false,
		},
	},
	[FilterOptionType.TABLE_NAME]: {
		label: 'Table name (Legacy)',
		type: FilterOptionType.TABLE_NAME,
		field: 'table',
		filterDropdownConfig: {
			multipleCountsSuffix: 'tables',
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Is,
			inputPlaceholder: 'Enter a table name',
			hasIsNotSetOption: false,
		},
	},
	[FilterOptionType.TAGS]: {
		label: 'Tags',
		type: FilterOptionType.TAGS,
		field: 'tags',
		filterDropdownConfig: {
			searchPlaceholder: 'Search tags',
			multipleCountsSuffix: 'tags',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: true,
			hasIsSetOption: true,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
					tagsQueryKeyFactory.list(filterPage, {
						name__icontains: filterSearchTerm,
						visible: true,
					});
				const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
					fetchTagList({
						filters: { name__icontains: filterSearchTerm, visible: true },
					});
				const filterFn = (filterSearchTerm: string, result: ITag[]) =>
					result.filter(
						(tag) =>
							!searchTerm ||
							tag.name.toLowerCase().includes(filterSearchTerm.toLowerCase())
					);

				const results = await fetchQuery<ITag>(
					getQueryKey,
					getQueryFn,
					page,
					searchTerm,
					filterFn
				);

				return (
					results?.map((tag: ITag) => ({
						label: startCase(tag.name),
						value: tag.id,
						icon: <TagIcon color={tag.color} />,
					})) ?? []
				);
			},
			getItemsById: buildGetItemsById(TAGS_NAMESPACE),
		},
	},
	[FilterOptionType.PUBLISHED]: {
		label: 'Published',
		type: FilterOptionType.PUBLISHED,
		field: 'published',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'Published',
					value: true,
					icon: (
						<Icon name="circleCheckFilled" fillColor="icon/success/default" />
					),
				},
				{
					label: 'Draft',
					value: false,
					icon: <Icon name="circle" color="icon/secondary/default" />,
				},
			],
		},
	},
	[FilterOptionType.VERIFICATION]: {
		label: 'Verification',
		type: FilterOptionType.VERIFICATION,
		field: 'verified',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'Verified',
					value: true,
					icon: <Icon name="verified" fillColor="fill/emphasis/default" />,
				},
				{
					label: 'Not verified',
					value: false,
					icon: <Icon name="verifiedUnfilled" color="icon/secondary/default" />,
				},
			],
		},
	},
	[FilterOptionType.PII]: {
		label: 'Governance',
		type: FilterOptionType.PII,
		field: 'pii',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'PII',
					value: true,
					icon: <Icon name="PII" fillColor="icon/caution/default" />,
				},
				{
					label: 'Non-PII',
					value: false,
					icon: <Icon name="notPII" />,
				},
			],
		},
	},
	[FilterOptionType.QUALITY_FLAGS]: {
		label: 'Quality Checks',
		type: FilterOptionType.QUALITY_FLAGS,
		field: 'quality_flags',
		filterDropdownConfig: {
			searchPlaceholder: 'Search quality flags',
			multipleCountsSuffix: 'quality flags',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: [
				{
					label: 'All columns documented',
					value: 'all_columns_documented',
				},
				{
					label: 'Schema documented',
					value: 'schema_documented',
				},
				{
					label: 'Questions answered',
					value: 'questions_answered',
				},
				{
					label: 'Exists: Freshness Monitors',
					value: 'monitor_exists_freshness',
				},
				{
					label: 'Exists: Nullness Monitors',
					value: 'monitor_exists_nullness',
				},
				{
					label: 'Exists: Uniqueness Monitors',
					value: 'monitor_exists_uniqueness',
				},
				{
					label: 'Passing: Freshness Monitors',
					value: 'monitor_passing_freshness',
				},
				{
					label: 'Passing: Nullness Monitors',
					value: 'monitor_passing_nullness',
				},
				{
					label: 'Passing: Uniqueness Monitors',
					value: 'monitor_passing_uniqueness',
				},
				{
					label: 'All monitors passing',
					value: 'all_monitors_passing',
				},
				{
					label: 'Has failing tests',
					value: 'has_failing_tests',
				},
			],
			convertToCatalogFilter: async (value: FilterValueType) => {
				if (!value) {
					return null;
				}

				const values = Array.isArray(value) ? value : [value];
				const filteredValues = values.filter((v) => v !== null) as string[];

				// Return null if no valid quality flags selected
				if (filteredValues.length === 0) {
					return null;
				}

				// Build operands for each selected quality flag
				const operands: Filter[] = filteredValues.map((flagName) => ({
					operands: [],
					operator: 'exact' as const,
					field: `quality_flags.${flagName}`,
					value: true,
				}));

				// Combine all flags with AND logic
				// This means entities must satisfy ALL selected quality flags
				return {
					operands,
					operator: 'and' as const,
				};
			},
			convertFromCatalogFilter: async (
				filter: Filter | null,
				isNegate: boolean = false
			): Promise<FilterValue | null> => {
				if (!filter) {
					return null;
				}

				// Quality flags filter should be an 'and' filter with operands
				if (filter.operator !== 'and' || !filter.operands?.length) {
					return null;
				}

				// Recursively extract quality flag names from nested structure
				const extractQualityFlags = (operands: Filter[]): string[] => {
					const flags: string[] = [];

					for (const operand of operands) {
						if (
							operand.field?.startsWith('quality_flags.') &&
							operand.operator === 'exact' &&
							operand.value === true
						) {
							// Direct quality flag field
							const flagName = operand.field.replace('quality_flags.', '');
							flags.push(flagName);
						} else if (operand.operands && operand.operands.length > 0) {
							// Nested structure - recurse into operands
							flags.push(...extractQualityFlags(operand.operands));
						}
					}

					return flags;
				};

				const qualityFlags = extractQualityFlags(filter.operands);

				if (qualityFlags.length === 0) {
					return null;
				}

				return {
					filterType: FilterOptionType.QUALITY_FLAGS,
					operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
					value: qualityFlags,
				};
			},
		},
	},
	[FilterOptionType.COLLECTIONS]: {
		label: 'Collections',
		type: FilterOptionType.COLLECTIONS,
		field: 'collections',
		filterDropdownConfig: {
			searchPlaceholder: 'Search collections',
			multipleCountsSuffix: 'collections',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: true,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
					collectionsQueryKeyFactory.list(filterPage, {
						filters: { search_term: filterSearchTerm },
					});
				const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
					fetchCollectionList({
						page: filterPage,
						filters: { search_term: filterSearchTerm },
					});
				const filterFn = (filterSearchTerm: string, result: ICollection[]) =>
					result.filter(
						(collection) =>
							!searchTerm ||
							collection.title
								?.toLowerCase()
								.includes(filterSearchTerm.toLowerCase())
					);

				const results = await fetchQuery<ICollection>(
					getQueryKey,
					getQueryFn,
					page,
					searchTerm,
					filterFn
				);

				return results?.map((collection: ICollection) => ({
					label: startCase(collection.title),
					value: collection.id,
					icon: (
						<IconWrapper>
							<ItemIcon
								type="emoji"
								item={{ icon: collection.icon }}
								color="icon/brand/default"
							/>
						</IconWrapper>
					),
				}));
			},
			getItemsById: buildGetItemsById(COLLECTIONS_NAMESPACE),
		},
	},
	[FilterOptionType.OWNERS]: {
		label: 'Owners',
		type: FilterOptionType.OWNERS,
		field: 'owners',
		filterDropdownConfig: {
			searchPlaceholder: 'Search owners',
			multipleCountsSuffix: 'owners',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: true,
			hasIsSetOption: true,
			getItems: allUsersAndGroupsGetItemsFn,
			getItemsById: buildGetItemsByIdFromSearch,
			convertToCatalogFilter: convertOwnerFilterToCatalogFilter,
			convertFromCatalogFilter: convertOwnerFilterFromCatalogFilter,
			additionalFields: ['owner_groups'],
			renderMenuItem: (item) => {
				const metadata = item.metadata as {
					type: string;
					disabled?: boolean;
					special?: boolean;
				};
				if (metadata?.disabled) {
					return <Text td="line-through">{item.label}</Text>;
				}
				return item.label;
			},
			isSetOverride: {
				operator: 'or',
				operands: [
					{
						operands: [],
						field: 'owners',
						operator: 'is_set',
						value: null,
					},
					{
						operands: [],
						field: 'owner_groups',
						operator: 'is_set',
						value: null,
					},
				],
			},
		},
	},
	[FilterOptionType.SOURCES]: {
		label: 'Metadata source',
		type: FilterOptionType.SOURCES,
		field: 'sources',
		filterDropdownConfig: {
			searchPlaceholder: 'Search sources',
			multipleCountsSuffix: 'sources',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				const getQueryKey = (filterPage: number, filterSearchTerm: string) =>
					integrationsQueryKeyFactory.list(filterPage, {
						search_term: filterSearchTerm,
					});
				const getQueryFn = (filterPage: number, filterSearchTerm: string) =>
					fetchIntegrationList({
						page: filterPage,
						filters: { search_term: filterSearchTerm },
					});
				const filterFn = (filterSearchTerm: string, result: IIntegration[]) =>
					result.filter(
						(integration) =>
							!searchTerm ||
							integration.name
								.toLowerCase()
								.includes(filterSearchTerm.toLowerCase())
					);

				const results = await fetchQuery<IIntegration>(
					getQueryKey,
					getQueryFn,
					page,
					searchTerm,
					filterFn
				);

				return map(
					results?.filter((integration) =>
						includes(['dbt', 'dbt_core', 'airflow'], integration.type)
					),
					(integration) => ({
						label: integration.name,
						value: integration.id,
						icon: (
							<IntegrationLogo
								entityType={EntityType.integration}
								integrationId={integration.id}
								integrationType={integration.type}
								size={16}
							/>
						),
					})
				);
			},
			getItemsById: buildGetItemsById(INTEGRATIONS_NAMESPACE),
		},
	},
	[FilterOptionType.PARENT_ID]: {
		label: 'Group',
		type: FilterOptionType.PARENT_ID,
		field: 'parent_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search groups',
			multipleCountsSuffix: 'groups',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => {
				const filters = await getEntityFilters();
				return uniqBy(filters.groups, 'value')
					.map((item) => ({
						label: item.label,
						value: item.value,
					}))
					?.filter(
						(item) =>
							!searchTerm ||
							item.label.toLowerCase().includes(searchTerm.toLowerCase())
					);
			},
		},
	},
	[FilterOptionType.RELATED]: {
		label: 'Related',
		type: FilterOptionType.RELATED,
		field: 'related',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			searchPlaceholder: 'Search related resources',
			getItems: async (page?: number, searchTerm?: string) => {
				if (!searchTerm) {
					return [];
				}

				const { results: searchResults } = (await getSearchResults(
					searchTerm,
					page
				)) as { results: SecodaEntity[]; totalPages: number };

				return searchResults.map((item) => ({
					label: getEntityDisplayTitle(item),
					value: item.id,
					metadata: item,
					icon: <SecodaEntityIcon entity={item} size={20} />,
				})) as FilterItem[];
			},
			getItemsById: buildGetItemsByIdFromSearch,
			renderMenuItem: renderFilterItemWithBreadcrumbs(true),
			disabledInNestedSearch: true,
		},
	},
	[FilterOptionType.SLACK_CHANNELS]: {
		label: 'Slack channel',
		type: FilterOptionType.SLACK_CHANNELS,
		field: 'slack_channels',
		filterDropdownConfig: {
			searchPlaceholder: 'Search slack channels',
			multipleCountsSuffix: 'channels',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page?: number, searchTerm?: string) => {
				const { queryKey, queryFn } = slackChannelsQuery(false);
				const query = await queryClient.fetchQuery({
					queryKey,
					queryFn,
				});

				return query
					?.map((channel: ISlackChannel) => ({
						label: startCase(channel.name),
						value: channel.name,
					}))
					?.filter(
						(item) =>
							!searchTerm ||
							item.label.toLowerCase().includes(searchTerm.toLowerCase())
					);
			},
		},
	},
	[FilterOptionType.QUESTION_STATUS]: {
		label: 'Status',
		type: FilterOptionType.QUESTION_STATUS,
		field: 'question_status',
		filterDropdownConfig: {
			searchPlaceholder: 'Search question status',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: [
				{
					label: 'Unanswered',
					value: 'UNANSWERED',
					icon: <Icon name="circle" color="icon/primary/default" />,
				},
				{
					label: 'In progress',
					value: 'IN_PROGRESS',
					icon: <Icon name="progress50" color="icon/caution/default" />,
				},
				{
					label: 'In review',
					value: 'IN_REVIEW',
					icon: <Icon name="progress75" color="icon/info/default" />,
				},
				{
					label: 'Answered',
					value: 'ANSWERED',
					icon: (
						<Icon name="circleCheckFilled" fillColor="icon/success/default" />
					),
				},
				{
					label: 'Cancelled',
					value: 'CANCELLED',
					icon: <Icon name="circleXFilled" fillColor="icon/primary/disabled" />,
				},
			],
		},
	},
	[FilterOptionType.QUESTION_PRIORITY]: {
		label: 'Priority',
		type: FilterOptionType.QUESTION_PRIORITY,
		field: 'question_priority',
		filterDropdownConfig: {
			searchPlaceholder: 'Search question priority',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: [
				{
					label: 'No priority',
					value: 'NONE',
					icon: <Icon name="priorityNone" color="icon/secondary/default" />,
				},
				{
					label: 'Low',
					value: 'LOW',
					icon: <Icon name="priorityLow" color="icon/secondary/default" />,
				},
				{
					label: 'Medium',
					value: 'MEDIUM',
					icon: <Icon name="priorityMedium" color="icon/secondary/default" />,
				},
				{
					label: 'High',
					value: 'HIGH',
					icon: <Icon name="priorityHigh" color="icon/secondary/default" />,
				},
			],
		},
	},
	[FilterOptionType.TITLE]: {
		label: 'Title',
		type: FilterOptionType.TITLE,
		field: 'title',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Contains,
			inputPlaceholder: 'Enter a title',
			hasIsNotSetOption: true,
			hasIsSetOption: true,
		},
	},
	[FilterOptionType.DESCRIPTION]: {
		label: 'Description',
		type: FilterOptionType.DESCRIPTION,
		field: 'description',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Contains,
			inputPlaceholder: 'Enter a description',
			hasIsNotSetOption: true,
			hasIsSetOption: true,
		},
	},
	[FilterOptionType.DOCUMENTATION]: {
		label: 'Documentation',
		type: FilterOptionType.DOCUMENTATION,
		field: 'definition',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.String,
			defaultOperator: FilterOperator.Contains,
			inputPlaceholder: 'Enter a text',
			hasIsNotSetOption: true,
		},
	},
	[FilterOptionType.TEAMS]: {
		label: 'Teams',
		type: FilterOptionType.TEAMS,
		field: 'teams',
		filterDropdownConfig: {
			searchPlaceholder: 'Search teams',
			multipleCountsSuffix: 'teams',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: true,
			convertToCatalogFilter: async (value) => {
				const values = Array.isArray(value) ? value : [value];
				const filteredValues = values.filter((v) => v !== null) as string[];

				// Return null if no valid teams selected
				if (filteredValues.length === 0) {
					return null;
				}

				// This logic handles the transition from teams-permissions-v1 to teams-permissions-v2.
				// v1: Entities could only have direct team assignments via the 'teams' field
				// v2: Entities can inherit teams from ancestors, descendants, and integrations
				// TODO: Once teams-permissions-v2 is fully launched:
				// 1. Remove 'teams' field from this OR filter
				// 2. Remove 'teams_granted.keyword' once ES index is fixed (search: "teams-granted-resolved-index-mismatch")
				// 3. Simplify to just use 'teams_granted' field with 'in' operator
				return {
					operands: [
						{
							operands: [],
							field: 'teams_granted', // v2: All teams (direct + inherited)
							operator: 'in',
							value: filteredValues,
						},
						{
							operands: [],
							field: 'teams_granted.keyword', // v2: ES compatibility fix
							operator: 'in',
							value: filteredValues,
						},
						{
							operands: [],
							field: 'teams', // v1: Direct teams only (backwards compatibility)
							operator: 'in',
							value: filteredValues,
						},
					],
					operator: 'or',
				};
			},
			getItems: async (page?: number, searchTerm?: string) => {
				const teams = await queryClient.fetchQuery({
					queryKey: apiListTeamsV2QueryKey({}),
					queryFn: ({ signal }) => fetchApiListTeamsV2({}, signal),
				});

				return teams
					?.map((team: TeamOut) => ({
						label: team.name,
						value: team.id,
						icon: <IconWrapper>{team.icon}</IconWrapper>,
					}))
					?.filter(
						(item: { label: string; value: string; icon: JSX.Element }) =>
							!searchTerm ||
							item.label.toLowerCase().includes(searchTerm.toLowerCase())
					);
			},
		},
	},
	[FilterOptionType.CREATED_TIME]: {
		label: 'Created time',
		type: FilterOptionType.CREATED_TIME,
		field: 'created_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DATE_LIST_OPTIONS,
			dateValueLabelFormatter: (
				value: FilterValueType | FilterValueType[]
			): string | null => {
				const valueToFind = Array.isArray(value) ? value[0] : value;

				if (!valueToFind || typeof valueToFind !== 'string') {
					return null;
				}

				const option = DATE_LIST_OPTIONS.find(
					(dateListOption) => dateListOption.value === valueToFind
				);

				return option?.label ?? valueToFind;
			},
		},
	},
	[FilterOptionType.UPDATED_TIME]: {
		label: 'Last updated',
		type: FilterOptionType.UPDATED_TIME,
		field: 'updated_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DATE_LIST_OPTIONS,
			dateValueLabelFormatter: (
				value: FilterValueType | FilterValueType[]
			): string | null => {
				const valueToFind = Array.isArray(value) ? value[0] : value;

				if (!valueToFind || typeof valueToFind !== 'string') {
					return null;
				}

				const option = DATE_LIST_OPTIONS.find(
					(dateListOption) => dateListOption.value === valueToFind
				);

				return option?.label ?? valueToFind;
			},
		},
	},
	[FilterOptionType.EXTERNALLY_UPDATED_TIME]: {
		label: 'Externally updated time',
		type: FilterOptionType.EXTERNALLY_UPDATED_TIME,
		field: 'external_updated_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DATE_LIST_OPTIONS,
			dateValueLabelFormatter: (
				value: FilterValueType | FilterValueType[]
			): string | null => {
				const valueToFind = Array.isArray(value) ? value[0] : value;

				if (!valueToFind || typeof valueToFind !== 'string') {
					return null;
				}

				const option = DATE_LIST_OPTIONS.find(
					(dateListOption) => dateListOption.value === valueToFind
				);

				return option?.label ?? valueToFind;
			},
		},
	},
	[FilterOptionType.FREQUENT_USERS]: {
		label: 'Frequent users',
		type: FilterOptionType.FREQUENT_USERS,
		field: 'frequent_users',
		filterDropdownConfig: {
			searchPlaceholder: 'Search users',
			multipleCountsSuffix: 'users',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: buildEntityGetItemsFn(EntityType.user),
			getItemsById: buildGetItemsByIdFromSearch,
		},
	},
	[FilterOptionType.AI_PERSONA]: {
		label: 'Persona',
		type: FilterOptionType.AI_PERSONA,
		field: 'persona_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search personas',
			multipleCountsSuffix: 'personas',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: true,
			hasIsSetOption: true,
			getItems: aiPersonasGetItemsFn,
			getItemsById: aiPersonasGetItemsById,
			convertToCatalogFilter: async (value: FilterValueType | null) => {
				if (value === null) {
					// Default persona (null) should match entities with no persona_id
					return {
						operands: [],
						operator: 'exact',
						field: 'persona_id',
						value: null,
					};
				}
				// Non-default personas filter by persona_id
				return {
					operands: [],
					operator: 'exact',
					field: 'persona_id',
					value,
				};
			},
		},
	},
	[FilterOptionType.TABLE]: {
		label: 'Table',
		type: FilterOptionType.TABLE,
		field: 'parent_id',
		filterDropdownConfig: {
			searchPlaceholder: 'Search tables',
			multipleCountsSuffix: 'tables',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: buildEntityGetItemsFn(EntityType.table),
			getItemsById: buildGetItemsByIdFromSearch,
			renderMenuItem: renderFilterItemWithBreadcrumbs(false),
			disabledInNestedSearch: true,

			convertToCatalogFilter: async (value: FilterValueType | null) => {
				if (!value) return null;
				const values = Array.isArray(value) ? value : [value];
				const ids = values.filter(Boolean) as string[];
				if (ids.length === 0) return null;

				const operands = ids.flatMap((v) => [
					{ operands: [], operator: 'exact' as const, field: 'id', value: v },
					{
						operands: [],
						operator: 'exact' as const,
						field: 'parent_id',
						value: v,
					},
				]);

				return { operator: 'or' as const, operands };
			},

			convertFromCatalogFilter: async (
				filter: Filter | null,
				isNegate: boolean = false
			) => {
				if (!filter) return null;

				if (
					filter.operator === 'exact' &&
					(filter.field === 'id' || filter.field === 'parent_id') &&
					typeof filter.value === 'string'
				) {
					try {
						const entity = await fetchSecodaEntity(filter.value);
						if (entity?.entity_type === 'table') {
							return {
								filterType: FilterOptionType.TABLE,
								value: entity.id,
								operator: isNegate ? FilterOperator.IsNot : FilterOperator.Is,
							};
						}
					} catch (e) {
						captureError(e);
					}
				}
				return null;
			},
		},
	},
	[FilterOptionType.DATA_QUALITY]: {
		label: 'Quality',
		type: FilterOptionType.DATA_QUALITY,
		field: 'dqs.total',
		filterDropdownConfig: {
			hasIsNotSetOption: true,
			defaultOperator: FilterOperator.Is,
			dropdownType: FilterDropdownType.List,
			convertToCatalogFilter: async (value: FilterValueType | null) => {
				if (!value) {
					return null;
				}

				return getDataQualityClassificationCatalogFilter(
					value as DataQualityClassification,
					'dqs.total'
				);
			},
			convertFromCatalogFilter: async (
				filter: Filter | null,
				isNegate?: boolean
			): Promise<FilterValue | null> => {
				if (!filter) {
					return null;
				}

				if (isNegate && filter.operator === 'is_set') {
					return {
						filterType: FilterOptionType.DATA_QUALITY,
						operator: FilterOperator.isNotSet,
						value: null,
						isNotSetApplied: true,
					};
				}

				if (typeof filter.value === 'number') {
					const classification = getScoreClassificationFromFilter(filter);

					return {
						filterType: FilterOptionType.DATA_QUALITY,
						value: classification,
						operator: FilterOperator.Is,
					};
				}

				return null;
			},
			getItems: [
				{
					label: 'Great',
					value: DataQualityClassification.GREAT,
				},
				{
					label: 'Good',
					value: DataQualityClassification.GOOD,
				},
				{
					label: 'Moderate',
					value: DataQualityClassification.MODERATE,
				},
				{
					label: 'Poor',
					value: DataQualityClassification.POOR,
				},
			],
		},
	},
	[FilterOptionType.IS_MONITORED]: {
		label: 'Has monitor',
		type: FilterOptionType.IS_MONITORED,
		field: 'monitors',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'Any monitor',
					value: 'any',
				},
				...MONITOR_SPECS.filter((spec) => !isAnalyticsMonitor(spec.type)).map(
					(spec) => ({
						label: spec.title,
						value: spec.type.metric_type,
					})
				),
			],
			convertToCatalogFilter: convertMonitorFilterToCatalogFilter(),
		},
	},
	[FilterOptionType.IS_TESTED]: {
		label: 'Has tests',
		type: FilterOptionType.IS_TESTED,
		field: 'all_tests_passing',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
			convertToCatalogFilter:
				convertBooleanFilterToIsSetFilter('all_tests_passing'),
		},
	},
	[FilterOptionType.TESTS_PASSING]: {
		label: 'Is passing tests',
		type: FilterOptionType.TESTS_PASSING,
		field: 'all_tests_passing',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
		},
	},
	[FilterOptionType.MONITORS_PASSING]: {
		label: 'Monitors passing',
		type: FilterOptionType.MONITORS_PASSING,
		field: 'passing_monitors',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
			convertToCatalogFilter:
				convertBooleanFilterToIsSetFilter('passing_monitors'),
		},
	},
	[FilterOptionType.LINEAGE]: {
		label: 'Lineage',
		type: FilterOptionType.LINEAGE,
		field: 'lineage',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: [
				{
					label: 'Upstream',
					value: 'UPSTREAM',
				},
				{
					label: 'Downstream',
					value: 'DOWNSTREAM',
				},
			],
			convertToCatalogFilter: convertLineageFilterToCatalogFilter(),
		},
	},
	[FilterOptionType.AI]: {
		label: 'AI filter',
		type: FilterOptionType.AI,
		field: 'ai', // TODO: fix this
		icon: <Icon name="sparkles" color="text/ai/default" />,
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.AI,
			defaultOperator: FilterOperator.Is,
		},
	},
	// Monitor measurements
	[FilterOptionType.HAS_INCIDENT]: {
		label: 'Incident occurred',
		type: FilterOptionType.HAS_INCIDENT,
		field: 'has_incident',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
		},
	},

	[FilterOptionType.MONITOR_METRIC_TYPE]: {
		label: 'Metric Type',
		type: FilterOptionType.MONITOR_METRIC_TYPE,
		field: 'metric_type',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: Object.values(METRIC_TYPE_INFORMATION).map((info) => ({
				label: info.label,
				value: info.value,
			})),
		},
	},
	[FilterOptionType.MONITOR_TARGET]: {
		label: 'Monitored Resource',
		type: FilterOptionType.MONITOR_TARGET,
		field: 'target_id',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			searchPlaceholder: 'Search targets',
			getItems: async (page?: number, searchTerm?: string) => {
				if (!searchTerm) {
					return [];
				}

				const { results: searchResults } = (await getSearchResults(
					searchTerm,
					page
				)) as { results: SecodaEntity[]; totalPages: number };

				return searchResults.map((item) => ({
					label: getEntityDisplayTitle(item),
					value: item.id,
					metadata: item,
					icon: <SecodaEntityIcon entity={item} size={20} />,
				})) as FilterItem[];
			},
			getItemsById: buildGetItemsByIdFromSearch,
			renderMenuItem: renderFilterItemWithBreadcrumbs(true),
			disabledInNestedSearch: true,
		},
	},
	[FilterOptionType.MONITOR_STATUS]: {
		label: 'Status',
		type: FilterOptionType.MONITOR_STATUS,
		field: 'status',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: Object.entries(MONITOR_ICON_MAPPING).map(([key, info]) => ({
				label: info.name,
				value: key,
				icon: (
					<Icon
						name={info.iconName}
						color={info.iconColor}
						fillColor={info.iconFillColor}
						size="md"
					/>
				),
			})),
		},
	},
	[FilterOptionType.INCIDENT_STATUS]: {
		label: 'Incident Status',
		type: FilterOptionType.INCIDENT_STATUS,
		field: 'incident_status',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'Open',
					value: 'active',
				},
				{
					label: 'Acknowledged',
					value: 'acknowledged',
				},
				{
					label: 'Resolved',
					value: 'resolved',
				},
				{
					label: 'Expired',
					value: 'expired',
				},
			],
		},
	},
	[FilterOptionType.REQUEST_STATUS]: {
		label: 'Status',
		type: FilterOptionType.REQUEST_STATUS,
		field: 'status',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: Object.entries(StatusColorMap)
				.filter(([value]) =>
					['approved', 'pending', 'rejected', 'cancelled'].includes(value)
				)
				.map(([value, config]) => ({
					label: config.label,
					value: value,
					icon: (
						<Icon
							name={config.iconName}
							color={config.iconColor}
							fillColor={config.iconFillColor}
						/>
					),
				})),
		},
	},
	[FilterOptionType.CREATED_BY]: {
		label: 'Created by',
		type: FilterOptionType.CREATED_BY,
		field: 'created_by',
		filterDropdownConfig: {
			searchPlaceholder: 'Search users',
			multipleCountsSuffix: 'users',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			hasIsNotSetOption: false,
			hasIsSetOption: false,
			getItems: createdByGetItemsFn,
			getItemsById: usersGetItemsByIdFn,
			convertToCatalogFilter: async (value: FilterValueType | null) => {
				if (!value) return null;
				const values = Array.isArray(value) ? value : [value];
				return {
					operands: [],
					operator: 'in',
					field: 'created_by',
					value: values as string[],
				};
			},
		},
	},
	[FilterOptionType.RUN_DATE]: {
		label: 'Run date',
		type: FilterOptionType.RUN_DATE,
		field: 'created_at',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Date,
			defaultOperator: FilterOperator.IsOnOrBefore,
		},
	},
	[FilterOptionType.DIRECTION]: {
		label: 'Direction',
		type: FilterOptionType.DIRECTION,
		field: 'direction',
		filterDropdownConfig: {
			searchPlaceholder: 'Search direction',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			hasIsSetOption: false,
			hasIsNotSetOption: false,
			getItems: [
				{
					label: 'Upstream',
					value: 'UPSTREAM',
				},
				{
					label: 'Downstream',
					value: 'DOWNSTREAM',
				},
			],
		},
	},
	[FilterOptionType.DEPTH]: {
		label: 'Depth',
		type: FilterOptionType.DEPTH,
		field: 'depth',
		filterDropdownConfig: {
			searchPlaceholder: 'Set depth limit...',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DEFAULT_DEPTH_ITEMS,
		},
	},
	[FilterOptionType.UPSTREAM_DEPTH]: {
		label: 'Upstream',
		type: FilterOptionType.UPSTREAM_DEPTH,
		field: 'upstream_depth',
		filterDropdownConfig: {
			searchPlaceholder: 'Set depth limit...',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DEFAULT_DEPTH_ITEMS,
		},
	},
	[FilterOptionType.DOWNSTREAM_DEPTH]: {
		label: 'Downstream',
		type: FilterOptionType.DOWNSTREAM_DEPTH,
		field: 'downstream_depth',
		filterDropdownConfig: {
			searchPlaceholder: 'Set depth limit...',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: DEFAULT_DEPTH_ITEMS,
		},
	},
	[FilterOptionType.SEVERITY]: {
		label: 'Severity',
		type: FilterOptionType.SEVERITY,
		field: 'severity',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: SELECTABLE_PROPERTY_OPTIONS.severity.map((item) => ({
				value: item.value as string,
				label: item.label as string,
				icon: (
					<Icon
						name={item.iconName as IconNames}
						color={item.color as ColorNames}
					/>
				),
			})),
		},
	},
	[FilterOptionType.FRAMEWORK]: {
		label: 'Frameworks',
		type: FilterOptionType.FRAMEWORK,
		field: 'frameworks',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: SELECTABLE_PROPERTY_OPTIONS.complianceFramework.map((item) => ({
				value: item.value as string,
				label: item.label as string,
			})),
		},
	},
	[FilterOptionType.ENABLED]: {
		label: 'Enabled',
		type: FilterOptionType.ENABLED,
		field: 'enabled',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
		},
	},
	[FilterOptionType.SYNC_STATUS]: {
		label: 'Sync Status',
		type: FilterOptionType.SYNC_STATUS,
		field: 'status',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{ label: 'Completed', value: 'success' },
				{ label: 'Failed', value: 'failed' },
				{ label: 'Partially Completed', value: 'partially_completed' },
				{ label: 'Cancelled', value: 'terminated' },
			],
		},
	},
	[FilterOptionType.POLICY]: {
		label: 'Policy',
		type: FilterOptionType.POLICY,
		field: 'policies.applied_policies',
		filterDropdownConfig: {
			searchPlaceholder: 'Search policies',
			multipleCountsSuffix: 'policies',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			getItems: async (page: number = 1, searchTerm: string = '') => {
				try {
					const response = await fetchApiListPolicies({
						queryParams: {
							search_term: searchTerm,
							page: page,
						},
					});

					return (
						response.results?.map(
							(policy: { id: string; name: string; icon?: string | null }) => ({
								label: policy.name,
								value: policy.id,
								icon: (
									<IconWrapper>
										<ItemIcon
											type="emoji"
											item={{ icon: policy.icon ?? '' }}
											color="icon/brand/default"
										/>
									</IconWrapper>
								),
							})
						) ?? []
					);
				} catch (error) {
					captureError(error);
					return [];
				}
			},
			getItemsById: buildGetItemsById(POLICIES_NAMESPACE),
		},
	},
	[FilterOptionType.POLICY_STATUS]: {
		label: 'Status',
		type: FilterOptionType.POLICY_STATUS,
		field: 'violating',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'Needs remediation',
					value: true,
					icon: (
						<Icon name="alertTriangleFilled" fillColor="icon/warning/default" />
					),
				},
				{
					label: 'OK',
					value: false,
					icon: (
						<Icon name="circleCheckFilled" fillColor="icon/success/default" />
					),
				},
			],
		},
	},
	[FilterOptionType.HAS_POLICY_ISSUE]: {
		label: 'Has policy issue',
		type: FilterOptionType.HAS_POLICY_ISSUE,
		field: 'policy_rules_failed',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: [
				{
					label: 'True',
					value: true,
				},
				{
					label: 'False',
					value: false,
				},
			],
			convertToCatalogFilter: convertBooleanFilterToIsSetFilter(
				'policy_rules_failed'
			),
		},
	},
	[FilterOptionType.POPULARITY]: {
		label: 'Popularity',
		type: FilterOptionType.POPULARITY,
		field: 'external_usage',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.Number,
			defaultOperator: FilterOperator.IsGreaterThan,
			inputPlaceholder: 'Enter a value',
		},
	},
	[FilterOptionType.ANY_CHILD]: {
		label: 'Any child',
		type: FilterOptionType.ANY_CHILD,
		field: 'any_child',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.NestedList,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: CHILD_FILTER_OPTIONS,
		},
	},
	[FilterOptionType.ALL_CHILDREN]: {
		label: 'All children',
		type: FilterOptionType.ALL_CHILDREN,
		field: 'all_children',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.NestedList,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: CHILD_FILTER_OPTIONS,
		},
	},
	[FilterOptionType.NO_CHILDREN]: {
		label: 'No children',
		type: FilterOptionType.NO_CHILDREN,
		field: 'no_children',
		filterDropdownConfig: {
			dropdownType: FilterDropdownType.NestedList,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Single,
			getItems: CHILD_FILTER_OPTIONS,
		},
	},
};

export const CATALOG_TYPE_FILTER_OPTION: FilterOption = {
	label: 'Type',
	type: FilterOptionType.NATIVE_TYPE,
	field: 'native_type',
	filterDropdownConfig: {
		searchPlaceholder: 'Search types',
		multipleCountsSuffix: 'types',
		dropdownType: FilterDropdownType.List,
		defaultOperator: FilterOperator.Is,
		getItems: async (page?: number, searchTerm?: string) => {
			const filters = await getEntityFilters();
			const nativeTypes = sortBy(uniqBy(filters.native_types, 'label'), [
				(nativeType) => lowerCase(nativeType.value as string),
			]).filter(
				(n) => n.entityType && CATALOG_ENTITY_TYPES.includes(n.entityType)
			);
			const nativeTypeFilters = nativeTypes?.map((n) => ({
				label: n.label,
				value: n.value,
				entityType: n.entityType,
			}));
			return uniqWith(
				nativeTypeFilters,
				(a, b) => a.value === b.value && a.entityType === b.entityType
			).filter(
				(item) =>
					!searchTerm ||
					item.label.toLowerCase().includes(searchTerm.toLowerCase())
			);
		},
	},
};
